import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, IconButton, Chip, TextField, InputAdornment, 
    MenuItem, FormControl, InputLabel, Select, TablePagination, Tooltip, 
    Snackbar, Alert, Avatar, Switch 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import UserFormModal from '../../components/UserFormModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import ExportFormatModal from '../../components/ExportFormatModal';
import ReplayIcon from '@mui/icons-material/Replay';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { 
    StandardStatusChip, 
    StandardRoleChip, 
    getAvatarColor, 
    getInitials,
    standardFilterContainerSx,
    standardSearchInputSx,
    standardFilterSelectSx,
    standardRefreshButtonSx,
    standardResetButtonSx,
    standardSecondaryButtonSx
} from '../../components/common/tableStyles';

const normalizeRole = (role) => {
    const roleName = typeof role === 'object' && role !== null ? role.name : role;
    return String(roleName || '').replace(/[_\s-]+/g, '').toUpperCase();
};



const formatStatus = (status) => {
    if (!status) return 'Active';
    const s = status.toUpperCase();
    if (s === 'ACTIVE') return 'Active';
    if (s === 'INACTIVE') return 'Inactive';
    return status;
};

const getStatusColor = (status) => {
    if (!status) return 'success';
    const s = status.toUpperCase();
    if (s === 'ACTIVE') return 'success';
    if (s === 'INACTIVE') return 'default';
    return 'error';
};

const isUserActive = (status) => {
    return !status || status.toUpperCase() === 'ACTIVE';
};

export default function Users() {
    const users = useSelector(state => state.users.list);
    const roles = useSelector(state => state.users.roles) || [];
    const hotels = useSelector(state => state.hotels.list) || [];
    const { user } = useSelector((state) => state.auth);
    const totalCount = useSelector(state => state.users.total);
    const isLoading = useSelector(state => state.users.isLoading);
    const dispatch = useDispatch();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [exportLoading, setExportLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const navigate = useNavigate();

    // Hidden file input reference
    const fileInputRef = React.useRef(null);

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');
    const [hotelFilter, setHotelFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');

    // Pagination States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    useEffect(() => {
        dispatch.users.fetchUsersAsync({
            search: searchTerm,
            page: page,
            limit: rowsPerPage,
            role: roleFilter,
            hotel: hotelFilter
        });
    }, [dispatch, searchTerm, page, rowsPerPage, roleFilter, hotelFilter]);

    useEffect(() => {
        dispatch.users.fetchRolesAsync();
        if (normalizeRole(user.role) === 'SUPERADMIN') {
            dispatch.hotels.fetchHotelsAsync({ noPagination: true }); // Fetch hotels for dropdowns
        }
    }, [dispatch, user.role]);

    // Apply status filter client-side if selected
    const filteredUsers = (users || []).filter(u => {
        if (statusFilter === 'All') return true;
        const active = isUserActive(u.status);
        return statusFilter === 'Active' ? active : !active;
    });

    // Reset pagination when filters change
    useEffect(() => {
        setPage(0);
    }, [searchTerm, roleFilter, hotelFilter, statusFilter]);

    const handleResetFilters = () => {
        setSearchTerm('');
        setRoleFilter('All');
        setHotelFilter('All');
        setStatusFilter('All');
    };

    const handleRefresh = () => {
        dispatch.users.fetchUsersAsync({
            search: searchTerm,
            page: page,
            limit: rowsPerPage,
            role: roleFilter,
            hotel: hotelFilter
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenAdd = () => {
        setEditingUser(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (user) => {
        setEditingUser(user);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingUser(null);
    };

    const refreshUsers = () => {
        return dispatch.users.fetchUsersAsync({
            search: searchTerm,
            page: page,
            limit: rowsPerPage,
            role: roleFilter,
            hotel: hotelFilter
        });
    };
    const getChangedFields = (original, updated) => {
        const changed = {};

        Object.keys(updated).forEach((key) => {
            if (updated[key] !== original[key]) {
                changed[key] = updated[key];
            }
        });

        return changed;
    };

    const handleSaveUser = async (userData) => {
        const selectedRole = roles.find(r => r.name === userData.role);
        const payload = { ...userData };
        if (selectedRole) {
            payload.roleId = selectedRole.id;
        }

        // Strip empty strings, nulls, and whitespace for non-mandatory fields
        Object.keys(payload).forEach(key => {
            if (typeof payload[key] === 'string') {
                payload[key] = payload[key].trim();
            }
            if (payload[key] === '' || payload[key] === null || payload[key] === 'null') {
                delete payload[key];
            }
        });

        try {
            if (editingUser) {
                const changedData = getChangedFields(editingUser, payload);
                if (userData.createLinkedStaff !== undefined) {
                    changedData.createLinkedStaff = userData.createLinkedStaff;
                }
                await dispatch.users.saveUserAsync({
                    id: editingUser.id, // important for PUT
                    ...changedData,
                });

                setNotification({
                    open: true,
                    message: 'User updated successfully!',
                    severity: 'success',
                });
            } else {
                await dispatch.users.saveUserAsync(payload);

                setNotification({
                    open: true,
                    message: 'User created successfully!',
                    severity: 'success',
                });
            }
            await refreshUsers();
        } catch (error) {
            setNotification({
                open: true,
                message: error.message || 'Failed to save user',
                severity: 'error',
            });
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteDialog({ open: true, userId: id });
    };

    const confirmDelete = async () => {
        if (deleteDialog.userId) {
            setDeleteLoading(true);
            try {
                await dispatch.users.deleteUserAsync(deleteDialog.userId);
                setNotification({ open: true, message: 'User deleted successfully!', severity: 'success' });
                await refreshUsers();
            } catch (error) {
                setNotification({ open: true, message: error.message || 'Failed to delete user', severity: 'error' });
            } finally {
                setDeleteLoading(false);
            }
        }
        setDeleteDialog({ open: false, userId: null });
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({ open: false, userId: null });
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const handleView = (id) => {
        navigate(`/users/${id}`);
    };

    const handleToggleStatus = async (user) => {
        const active = isUserActive(user.status);
        const newStatus = active ? 'Inactive' : 'Active';
        try {
            await dispatch.users.toggleStatusAsync({ id: user.id, status: newStatus });
            setNotification({ open: true, message: `User status changed to ${newStatus}`, severity: 'success' });
            await refreshUsers();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update status', severity: 'error' });
        }
    };

    const handleExport = async (format) => {
        setExportModalOpen(false);
        setExportLoading(true);
        try {
            await dispatch.users.exportUsersAsync(format);
            setNotification({ open: true, message: 'Export successful', severity: 'success' });
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to export', severity: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImportLoading(true);
        try {
            // Validate first
            const validation = await dispatch.users.importValidateAsync(file);
            if (validation.success && validation.data) {
                // If valid, execute import
                await dispatch.users.importExecuteAsync({records:validation.data.validRecords});
                setNotification({ open: true, message: 'Import successful', severity: 'success' });
                await refreshUsers();
            }
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Import failed', severity: 'error' });
        } finally {
            setImportLoading(false);
            e.target.value = ''; // reset input
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Users Management</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        accept=".csv" 
                        onChange={handleFileChange} 
                    />
                    <Button 
                        variant="outlined" 
                        startIcon={importLoading ? <CircularProgress size={18} color="inherit" /> : <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />} 
                        onClick={handleImportClick} 
                        disabled={importLoading}
                        sx={standardSecondaryButtonSx}
                    >
                        Import CSV
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={exportLoading ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => setExportModalOpen(true)} 
                        disabled={exportLoading}
                        sx={standardSecondaryButtonSx}
                    >
                        Export
                    </Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleExport}
                        isExporting={exportLoading}
                        title="Export Users"
                    />
                    <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 18 }} />} onClick={handleOpenAdd}>
                        Add New User
                    </Button>
                </Box>
            </Box>

            {/* Filters Section */}
            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={roleFilter}
                            displayEmpty
                            onChange={(e) => setRoleFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Roles</MenuItem>
                            {roles.map(r => (
                                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={hotelFilter}
                            displayEmpty
                            onChange={(e) => setHotelFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Hotels</MenuItem>
                            {hotels.map(h => (
                                <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={statusFilter}
                            displayEmpty
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Status</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Inactive">Inactive</MenuItem>
                        </Select>
                    </FormControl>

                    <IconButton 
                        onClick={handleRefresh}
                        title="Refresh"
                        sx={standardRefreshButtonSx}
                    >
                        <RefreshIcon sx={{ fontSize: 18 }} />
                    </IconButton>

                    {(searchTerm || roleFilter !== 'All' || hotelFilter !== 'All' || statusFilter !== 'All') && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                            onClick={handleResetFilters}
                            sx={standardResetButtonSx}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                                    Name <SwapVertIcon sx={{ fontSize: 16, color: '#98a2b3' }} />
                                </Box>
                            </TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Hotel</TableCell>
                            <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                                    Last Login <SwapVertIcon sx={{ fontSize: 16, color: '#98a2b3' }} />
                                </Box>
                            </TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : filteredUsers?.length > 0 ? (
                            filteredUsers.map((user) => {
                                const avatarColor = getAvatarColor(user.name || user.email);
                                return (
                                    <TableRow 
                                        key={user.id} 
                                        hover 
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar 
                                                    sx={{ 
                                                        width: 36, 
                                                        height: 36, 
                                                        bgcolor: avatarColor.bg, 
                                                        color: avatarColor.color,
                                                        fontSize: '0.8125rem',
                                                        fontWeight: 700,
                                                        border: `1px solid ${avatarColor.bg}`
                                                    }}
                                                >
                                                    {getInitials(user.name)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#101828', lineHeight: 1.3 }}>
                                                        {user.name ? user.name.charAt(0).toUpperCase() + user.name.slice(1) : "N/A"}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.75rem' }}>
                                                        {user.email || ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <StandardRoleChip role={user?.role?.name || user.role} />
                                        </TableCell>
                                        <TableCell>
                                            {user.propertyNames && user.propertyNames.length > 0 ? (
                                                user.propertyNames.length <= 2 ? (
                                                    <Typography variant="body2" sx={{ color: '#344054', fontSize: '0.875rem' }}>
                                                        {user.propertyNames.join(', ')}
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="body2" sx={{ color: '#344054', fontSize: '0.875rem' }}>
                                                            {user.propertyNames.slice(0, 2).join(', ')}
                                                        </Typography>
                                                        <Tooltip title={user.propertyNames.join(', ')} arrow>
                                                            <Chip 
                                                                label={`+${user.propertyNames.length - 2}`} 
                                                                size="small" 
                                                                sx={{ 
                                                                    ml: 1, 
                                                                    height: 20, 
                                                                    fontSize: '0.7rem', 
                                                                    bgcolor: '#eff8ff', 
                                                                    color: '#175cd3',
                                                                    fontWeight: 600
                                                                }} 
                                                            />
                                                        </Tooltip>
                                                    </Box>
                                                )
                                            ) : (user?.assignedHotel?.name || user.hotel || 'N/A')}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#475467', fontSize: '0.8125rem' }}>
                                                {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StandardStatusChip status={formatStatus(user?.status)} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <Switch 
                                                    size="small" 
                                                    checked={isUserActive(user?.status)} 
                                                    onChange={() => handleToggleStatus(user)}
                                                    title={isUserActive(user?.status) ? "Deactivate User" : "Activate User"}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#12b76a',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#12b76a',
                                                        },
                                                    }}
                                                />
                                                <IconButton 
                                                    size="small" 
                                                    sx={{ color: '#98a2b3', '&:hover': { color: '#475467' } }} 
                                                    onClick={() => handleView(user.id)} 
                                                    title="View User"
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton 
                                                    size="small" 
                                                    sx={{ color: '#98a2b3', '&:hover': { color: '#475467' } }} 
                                                    onClick={() => handleOpenEdit(user)} 
                                                    title="Edit User"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton 
                                                    size="small" 
                                                    sx={{ color: '#98a2b3', '&:hover': { color: 'error.main' } }} 
                                                    onClick={() => handleDeleteClick(user.id)} 
                                                    title="Delete User"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No users found matching your filters.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[20, 50, 100]}
                    component="div"
                    count={filteredUsers?.length === 0 ? 0 : (totalCount || filteredUsers?.length || 0)}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => `Showing ${from}-${to} of ${count !== -1 ? count : `more than ${to}`} members`}
                />
            </TableContainer>

            <UserFormModal
                open={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveUser}
                editingUser={editingUser}
                roles={roles}
                hotels={hotels}
            />

            <ConfirmDeleteModal
                open={deleteDialog.open}
                onClose={closeDeleteDialog}
                onConfirm={confirmDelete}
                itemName="user"
                isLoading={deleteLoading}
            />

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
